<template>
  <div class="editor-main">
    <div class="menubar-area">
      <div class="menubar-logo" @click="goHome">
        <img src="../assets/logo_wTVision_small.png" alt="">
        <span>ision</span>
      </div>
      <div class="menubar-items">
        <div class="menubar-user">
          <img src="../assets/admin.png" alt="" title="Administrator Dashboard" @click="navToAdmin">
        </div>
        <div class="menubar-user">
          <img src="../assets/logout.png" alt="" title="Logout" @click="navToLogout">
        </div>
      </div>
    </div>
    <div class="editor-area">
      <div class="middle-side" ref="middleRef">
        <span v-if="loading" class="loader">Load&nbsp;ng</span>
        <span v-if="loading" class="downloader"></span>

        <ImageCanvas v-if="currentImage" :image="currentImage" :highLightedLine="highlightedLine" :readOnly="true" />
        <div v-if="currentImage?.is_non_trackable" class="untrackable-overlay">UNTRACKABLE</div>

        <div v-if="!currentImage && !loading" style="color:#aaa; margin-top:8px;">
          No Images to Review
        </div>

        <div class="pending-counter" v-if="!loading">
          To Validate: <strong>{{ pendingCount }}</strong>
        </div>

        <div class="review-actions" ref="actionsRef">
          <button class="icon-btn" :disabled="!canGoBack || loading || busy" @click="goBack" title="Back">
            <img src="../assets/back.png" alt="Back" />
          </button>

          <button v-if="currentImage && !currentImage.is_validated" class="icon-btn" :disabled="!currentImage || loading || busy" @click="validate" title="Validate">
            <img src="../assets/validate.png" alt="Validate" />
          </button>

          <button v-if="currentImage && !currentImage.is_validated" class="icon-btn" :disabled="!currentImage" @click="openInvalidate" title="Invalidate">
            <img src="../assets/invalidate.png" alt="Invalidate" />
          </button>

          <button v-else-if="currentImage && currentImage.is_validated" class="icon-btn" :disabled="!currentImage || loading || busy" @click="unvalidate" title="Invalidate">
            <img src="../assets/invalidate.png" alt="Invalidate" />
          </button>

          <button class="icon-btn" :disabled="loading || busy" @click="nextImage" title="Next">
            <img src="../assets/next.png" alt="Next" />
          </button>
        </div>

        <div v-if="invalidateOpen"
            ref="modalRef"
            class="invalidate-popover"
            :style="modalStyle"
            role="dialog"
            aria-modal="true">
          <h3>Notes:</h3>
          <textarea ref="notesRef"
                    v-model="invalidateNotes"
                    rows="4"
                    placeholder="Add optional notes here..."></textarea>
          <div class="modal-actions">
            <button @click="confirmInvalidate">Ok</button>
            <button @click="cancelInvalidate">Cancel</button>
          </div>
          <div class="popover-arrow"></div>
        </div>
      </div>

      <div class="right-side-bar">
        <div class="labels-container">
          <Labels v-if="currentImage" :image="currentImage" :readOnly="true" mode="review" :wrongIds="Array.from(wrongIds)" @onLabelFocus="onLabelFocus" @markWrong="onMarkWrong"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, onBeforeUnmount, computed, nextTick } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { supabase } from "@/supabase";
import ImageCanvas from "@/components/ImageCanvas.vue";
import Labels from "@/components/Labels.vue";
import ProjectService, { extractErr } from "@/services/Project";
import { ImageInfo, Line, Project } from "@/helpers/Interfaces";
import { subscriptionManager } from "@/realtime/SubscriptionManager";

export default defineComponent({
  components: { ImageCanvas, Labels },
  setup(){
    const store = useStore();
    const router = useRouter();
    const currentProject = computed<Project>(() => store.state.currentProject);
    const currentImage = ref<ImageInfo|null>(null);
    const highlightedLine = ref<Line|null>(null);
    const pendingCount = ref<number>(0);
    const loading = ref<boolean>(false);

    const middleRef  = ref<HTMLElement|null>(null);
    const actionsRef = ref<HTMLElement|null>(null);
    const modalRef   = ref<HTMLElement|null>(null);
    const notesRef   = ref<HTMLTextAreaElement|null>(null);
    const modalStyle = ref<{left:string; top:string; minWidth?:string}>({ left: '0px', top: '0px' });

    function clamp(n:number, min:number, max:number){ return Math.max(min, Math.min(max, n)); }
    const nextFrame = () => new Promise(requestAnimationFrame);

    const invalidateOpen = ref(false);
    const invalidateNotes = ref<string>("");

    const wrongIds = ref<Set<number>>(new Set());
    const busy = ref<boolean>(false);

    const projSub = ref<{ __type: string; offs: Array<() => void> } | null>(null);

    const withBusy = async <T>(fn: () => Promise<T>): Promise<T | undefined> => {
      if (busy.value) return;
      busy.value = true;
      try { return await fn(); }
      finally { busy.value = false; }
    };

    const nextImageCore = async () => {
      const avoid = currentImage.value ? [currentImage.value.id] : [];
      if (currentImage.value) {
        try { await (ProjectService as any).releaseReviewLock(currentImage.value.id); } catch {}
      }
      await claimOne(avoid);
    };

    const history = ref<ImageInfo[]>([]);
    const historyIndex = ref<number>(-1); // aponta para currentImage em history
    const canGoBack = computed(() => historyIndex.value > 0);

    let hb: number | null = null;
    let beforeUnloadHandler: ((e: BeforeUnloadEvent) => void) | null = null;
    
    function onLabelFocus(line: Line|null){ highlightedLine.value = line; }

    async function subscribeToProjectImages(projectId: number, cb: (event: "insert" | "delete" | "has_lines" | "validated" | "invalidated", image: ImageInfo) => void) {
      const topic = subscriptionManager.topicImages(projectId);
      const offs: Array<() => void> = [];

      offs.push(await subscriptionManager.subscribe(topic, "images:insert",     (p: any) => cb("insert",      p.image)));
      offs.push(await subscriptionManager.subscribe(topic, "images:delete",     (p: any) => cb("delete",      p.image)));
      offs.push(await subscriptionManager.subscribe(topic, "images:has_lines",  (p: any) => cb("has_lines",   p.image)));
      offs.push(await subscriptionManager.subscribe(topic, "images:validated",  (p: any) => cb("validated",   p.image)));
      offs.push(await subscriptionManager.subscribe(topic, "images:invalidated",(p: any) => cb("invalidated", p.image)));

      return { __type: "images-subs", offs };
    }

    /** Desfaz as subscrições criadas acima. */
    async function unsubscribeToProjectImages(handle: { __type: string; offs: Array<() => void> } | null) {
      if (!handle || handle.__type !== "images-subs") return;
      try {
        handle.offs.forEach(off => off());
      } catch {}
    }

    async function updatePendingCount() {
      if (!currentProject.value?.id) return;

      try {
        pendingCount.value = await (ProjectService as any).getPendingReviewCount(currentProject.value.id);
      } catch (e) {
        console.error("Error getting pending count:", e);
      }
    }

    async function claimOne(avoidIds: number[] = []) {
      if (!currentProject.value?.id) return;
      loading.value = true;
      try {
        const exclude = Array.from(new Set(avoidIds.filter((v): v is number => Number.isInteger(v))));
        
        let attempts = 0;
        let img: ImageInfo | null = null;

        while (attempts < 5) {
          attempts++;
          try {
            img = await (ProjectService as any).claimNextImageForReview(currentProject.value.id, exclude);
          } catch (e) {
            console.error("claimNextImageForReview failed:", e);
            img = null;
            break;
          }

          if (!img) break;

          if (exclude.includes(img.id)) {
            try { await (ProjectService as any).releaseReviewLock(img.id); } catch {}
            img = null;
            continue;
          }
          break; // ok
        }

        setCurrentFromClaim(img, true)
      } catch (e) {
        console.error("claimOne error:", e);
        currentImage.value = null;
      } finally {
        loading.value = false;
        await updatePendingCount();
      }
    }

    async function goBack() {
      return withBusy(async () => {
        if (!canGoBack.value) return;

        // larga o lock atual
        if (currentImage.value) {
          try { await (ProjectService as any).releaseReviewLock(currentImage.value.id); } catch {}
        }

        while (historyIndex.value > 0) {
          const prev = history.value[historyIndex.value - 1];
          try {
            const row = await (ProjectService as any).claimImageForReviewById(prev.id);
            if (row) {
              history.value[historyIndex.value - 1] = row; // atualiza estado (ex.: is_validated)
              historyIndex.value -= 1;
              currentImage.value = row;
              await updatePendingCount();
              return;
            }
          } catch {
            // falhou, tenta mais atrás
            historyIndex.value -= 1;
          }
        }

        // nada encontrado
        currentImage.value = null;
        await updatePendingCount();
      });
    }

    async function nextImage(){
      return withBusy(async () => { await nextImageCore(); });
    }

    async function validate(){
      return withBusy(async () => {
        if (!currentImage.value) return;

        try {

          const { data: { session } } = await supabase.auth.getSession();
          await (ProjectService as any).validateImage(currentImage.value.id);

          if (historyIndex.value >= 0) {
            history.value[historyIndex.value] = {
              ...history.value[historyIndex.value],
              is_validated: true,
              validated_by: session?.user.email,
              validated_at: new Date().toISOString()
            } as any;
          }
          currentImage.value.is_validated = true;

          await updatePendingCount();

          await nextImageCore();
        } catch (e) {
          console.error('validate failed:', e);
          alert(extractErr(e));
        }
      });
    }

    async function unvalidate() {
      return withBusy(async () => {
        if (!currentImage.value) return;

        try {
          await (ProjectService as any).unvalidateImage(currentImage.value.id);
          currentImage.value.is_validated = false;

          if (historyIndex.value >= 0) {
            history.value[historyIndex.value] = {
              ...history.value[historyIndex.value],
              is_validated: false,
              validated_by: null,
              validated_at: null
            } as any;
          }

          await updatePendingCount();
        } catch (e) {
          console.error('unvalidate failed:', e);
          alert(extractErr(e));
        }
      });
    }

    function openInvalidate(){
      invalidateOpen.value = true;
      nextTick(async () => {
        await positionInvalidatePopover();
        // foco na textarea
        notesRef.value?.focus();
        // listeners para ESC / resize / click fora
        window.addEventListener('keydown', onEsc, { passive: true });
        window.addEventListener('resize', positionInvalidatePopover);
        document.addEventListener('click', onDocClick, true);
      });
    }

    function cancelInvalidate(){
      invalidateOpen.value = false;
      invalidateNotes.value = "";
      window.removeEventListener('keydown', onEsc);
      window.removeEventListener('resize', positionInvalidatePopover);
      document.removeEventListener('click', onDocClick, true);
    }

    function onEsc(e: KeyboardEvent){
      if (e.key === 'Escape' && invalidateOpen.value) cancelInvalidate();
    }

    function onDocClick(e: MouseEvent){
      // Closes on click outside of popup
      const el = modalRef.value;
      if (!el) return;
      if (!el.contains(e.target as Node)) {
        cancelInvalidate();
      }
    }

    async function confirmInvalidate() {
      if (!currentImage.value) return;
      try {
        // 1) grava as linhas marcadas como erradas
        const toSave = Array.from(wrongIds.value);
        if (toSave.length) {
          await Promise.all(
            toSave.map(id =>
              (ProjectService as any).markAnnotationWrong(
                currentImage.value!.id, id, (invalidateNotes.value || "")
              )
            )
          );
        }

        // 2) invalida a imagem
        await (ProjectService as any).invalidateImage(currentImage.value.id, invalidateNotes.value || "");

        // 3) UI local (opcional, para feedback imediato)
        currentImage.value = {
          ...currentImage.value,
          is_invalidated: true,
          invalidate_notes: invalidateNotes.value
        } as any;

        // 4) limpar estado temporário e seguir
        resetWrongMarks();
        await updatePendingCount();
        invalidateOpen.value = false;
        invalidateNotes.value = "";
        await nextImage();
      } catch (e) {
        console.error("Erro a invalidar:", e);
      }
    }

    function setCurrentFromClaim(img: ImageInfo|null, pushToHistory = true) {
      resetWrongMarks();
      currentImage.value = img;
      if (img && pushToHistory) {
        if (historyIndex.value < history.value.length - 1) {
          history.value.splice(historyIndex.value + 1);
        }
        history.value.push(img);
        historyIndex.value = history.value.length - 1;
      }
    }

    async function onMarkWrong(line: Line) {
      if (!line?.id) return;
      if (wrongIds.value.has(line.id)) wrongIds.value.delete(line.id);
      else wrongIds.value.add(line.id);
    }

    async function positionInvalidatePopover() {
      await nextTick();
      await nextFrame();

      const middle = middleRef.value;
      const actions = actionsRef.value;
      const modal = modalRef.value;
      if (!middle || !actions || !modal) return;

      const mid = middle.getBoundingClientRect();
      const act = actions.getBoundingClientRect();
      let box = modal.getBoundingClientRect();
      if (box.width === 0 || box.height === 0) {
        // fallback to offsetWidth/Height
        const w = modal.offsetWidth || 360;
        const h = modal.offsetHeight || 120;
        box = { ...box, width: w, height: h } as DOMRect;
      }      

      // Centro horizontal no meio da barra de botões
      const centerX = act.left + act.width / 2;
      let left = centerX - box.width / 2 - mid.left;
      left = clamp(left, 8, mid.width - box.width - 8);

      // Por cima da barra de botões (8px de gap)
      const gap = 8;
      const top = (act.top - mid.top) - box.height - gap;

      modalStyle.value = { left: `${left}px`, top: `${Math.max(8, top)}px`, minWidth: '340px' };
    }

    function resetWrongMarks() {
      wrongIds.value = new Set(); 
    }

    function goHome(){ router.push({ name: "home" }); }

    function navToAdmin() {
      router.push({ name: "adminDashboard" });
    }

    async function navToLogout() {
      try {
        await supabase.auth.signOut();
      } catch (e) {
        console.error("Error on logout:", e);
      } finally {
        router.push({ name: "home" });
      }
    }

    onMounted(async () => {
      try {
        if (!currentProject.value?.id) {
          router.push({ name: "home" });
          return;
        }

        await claimOne();
        projSub.value = await subscribeToProjectImages(
          currentProject.value.id,
          async (event, image) => {
            if (event === "validated") {
              if (currentImage.value?.id === image.id) {
                currentImage.value = {
                  ...currentImage.value,
                  is_validated: image.is_validated,
                  validated_at: image.validated_at,
                  validated_by: image.validated_by
                } as any;
              }
              await updatePendingCount();
            }

            if (event === "invalidated") {
              if (currentImage.value?.id === image.id) {
                currentImage.value = { ...currentImage.value, ...image } as any;
              }
              await updatePendingCount();
            }
          }
        );

        hb = window.setInterval(async () => {
          if (currentImage.value) {
            try { await (ProjectService as any).heartbeatReviewLock(currentImage.value.id); } catch {}
          }
        }, 120000);

        if (!beforeUnloadHandler) {
          beforeUnloadHandler = (e: BeforeUnloadEvent) => {
            if (currentImage.value) {

              (ProjectService as any).releaseReviewLock(currentImage.value.id).catch(() => {});
            }
          };
          window.addEventListener("beforeunload", beforeUnloadHandler);
        }

        const ro = new ResizeObserver(() => { if (invalidateOpen.value) positionInvalidatePopover(); });
        if (middleRef.value) ro.observe(middleRef.value);
        (window as any).__rv_ro = ro;
      } catch (e) {
        console.error("[ReviewView] onMounted failed:", e);
        loading.value = false;
      }
    });

    onBeforeUnmount(async () => {
      if (hb) { clearInterval(hb); hb = null; }
      if (beforeUnloadHandler) {
        window.removeEventListener("beforeunload", beforeUnloadHandler);
        beforeUnloadHandler = null;
      }
      if (currentImage.value) {
        try { await (ProjectService as any).releaseReviewLock(currentImage.value.id); } catch {}
      }

      if (projSub.value) {
        await unsubscribeToProjectImages(projSub.value); // ← função local
        projSub.value = null;
      }

      try { (window as any).__rv_ro?.disconnect(); } catch {}
      window.removeEventListener('keydown', onEsc);
      window.removeEventListener('resize', positionInvalidatePopover);
      document.removeEventListener('click', onDocClick, true);      
    });

    return { currentImage, highlightedLine, onLabelFocus, nextImage, validate, unvalidate, goHome, goBack, navToAdmin,
             navToLogout, pendingCount, loading, canGoBack, busy, openInvalidate, cancelInvalidate, invalidateOpen,
             invalidateNotes, confirmInvalidate, onMarkWrong, middleRef, actionsRef, modalRef, modalStyle, notesRef,
             resetWrongMarks, wrongIds };
  }
});
</script>

<style scoped>
  .editor-main {
    display: flex;
    flex-direction: column;
    height: 100vh;
    width: 100vw;
  }

  .menubar-area {
    display: flex;
    flex-direction: row;
    height: 35px;
    width: 100%;
    background: #252525;
    border-bottom: 2px solid #9e9e9e;
    flex-shrink: 0;
  }

  .menubar-logo {
    display: flex;
    justify-content: start;
    align-items: center;
    margin-left: 20px;
  }

  .menubar-logo img { 
    width: 30px;
    height: auto; 
  }

  .menubar-logo span {
     color: white;
     font-family: sans-serif;
     font-weight: bold; 
   }

  .menubar-logo img:hover, .menubar-logo label:hover {
    cursor: pointer;
  }

  .menubar-items {
    padding-left: 15px;
    display: flex;
    align-items: center;
    margin-left: 20px;
    margin-top: 2px;
    color: rgb(192, 192, 192);
  }
  .menubar-items img {
    width: 25px;
    height: auto;
    padding-top: 4px;
    padding-left: 8px;
    padding-right: 4px;
    transition: background-color 1.0s;
  }
  .menubar-items img:hover {
     background: #565656; 
  }

  .editor-area {
    display: flex;
    flex-direction: row;
    flex-grow: 1;
    min-height: 0;
    background: #151515;
  }

  /* Sem thumbnails; só zona central + labels à direita */
  .middle-side {
    flex-grow: 1;
    height: 100%;
    display: flex;
    flex-direction: column;         /* Para os botões ficarem por baixo da imagem */
    align-items: center;            /* Centrar horizontalmente */
    justify-content: center;        /* Centrar verticalmente (para a própria área) */
    background-color: #151515;
    border-left: 2px solid #4d4d4d;
    border-right: 2px solid #4d4d4d;
    position: relative;
  }

  /* A barra da direita com Labels (igual ao EditorView) */
  .right-side-bar {
    flex-shrink: 0;
    width: 250px;
    height: 100%;
    background-color: #282828;
    display: flex;
    align-items: center;
    justify-content: start;
    flex-direction: column;
  }

  .labels-container {
    display: flex;
    width: 100%;
    height: 98.5%;
    align-items: start;
    justify-content: center;
    flex-shrink: 0;
  }

  /* Botões “Back / Validate / Next” */
  .review-actions {
    display: flex;
    gap: 14px;
    margin-top: 12px;
    align-items: center;
    justify-content: center;
  }

  .icon-btn {
    border: 1px solid #4d4d4d;
    background: #2b2b2b;
    border-radius: 6px;
    padding: 6px 10px;
    cursor: pointer;
  }

  .icon-btn[disabled] {
    opacity: .4;
    cursor: not-allowed;
  }

  .icon-btn img {
    width: 22px;
    height: 22px;
  }

  /* Contador (por validar) */
  .pending-counter {
    position: absolute;
    bottom: 56px; /* fica acima da linha dos botões */
    left: 50%;
    transform: translateX(-50%);
    color: #d0d0d0;
    font-size: 13px;
    background: rgba(40,40,40,.6);
    padding: 4px 10px;
    border-radius: 6px;
    border: 1px solid rgba(255,255,255,.08);
  }

  /* Loaders (copiados do EditorView para consistência) */
  .loader {
    z-index: 5;
    color: #FFF;
    display: inline-block;
    position: absolute;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 48px;
    letter-spacing: 4px;
    box-sizing: border-box;
  }

  .loader::before {
    content: '';
    position: absolute;
    right: 68px;
    bottom: 10px;
    height: 24px;
    width: 7px;
    outline: 1px solid #FFF;
    color: #3769c5;
    box-sizing: border-box;
    animation: animloader1 1s linear infinite alternate;
  }

  .loader::after {
    content: '';
    width: 4px;
    height: 4px;
    background: #3769c5;
    position: absolute;
    right: 70px;
    top: 8px;
    box-sizing: border-box;
    animation: animloader 1s ease-out infinite alternate;
  }

  @keyframes animloader {
    0% { top: 8px; transform: rotate(0deg) scale(1); }
    100% { top: 0px; transform: rotate(180deg) scale(1.5); }
  }
  @keyframes animloader1 {
    0% { box-shadow: 0 0 inset; }
    100% { box-shadow: 0 -28px inset; }
  }

  .downloader {
    z-index: 5;
    width: 48px;
    height: 12px;
    background: #FFF;
    display: inline-block;
    position: absolute;
    margin-top: 10%;
  }

  .downloader::after {
    content: '';
    left: 50%;
    bottom: 0;
    transform: translate(-50%, 0);
    position: absolute;
    border: 15px solid transparent;
    border-top-color: #FFF;
    box-sizing: border-box;
    animation: bump 0.4s ease-in-out infinite alternate;
  }

  .downloader::before {
    content: '';
    left: 50%;
    bottom: 25px;
    transform: translate(-50%, 0);
    position: absolute;
    width: 15px;
    height: 20px;
    background: #FFF;
    box-sizing: border-box;
    animation: bump 0.4s ease-in-out infinite alternate;
  }

  @keyframes bump {
    0% { transform: translate(-50%, 5px); }
    100% { transform: translate(-50%, -5px); }
  }

  .invalidate-popover {
    position: absolute;
    z-index: 1200;
    background: #2b2b2b;
    color: #e5e5e5;
    border: 1px solid rgba(255,255,255,0.12);
    border-radius: 8px;
    padding: 12px;
    box-shadow: 0 10px 30px rgba(0,0,0,0.45);
    max-width: min(90vw, 520px);
    animation: popIn 120ms ease-out;
  }

  .invalidate-popover h3 {
    margin: 0 0 8px 0;
    font-size: 14px;
    color: #f3f4f6;
  }

  .invalidate-popover textarea {
    width: 100%;
    box-sizing: border-box;
    background: #1f1f1f;
    color: #ddd;
    border: 1px solid #3b3b3b;
    border-radius: 6px;
    padding: 8px;
    resize: vertical;
    min-height: 84px;
    outline: none;
  }

  .invalidate-popover .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 10px;
  }

  .invalidate-popover .modal-actions button {
    background: #3a3a3a;
    color: #fff;
    border: 1px solid #555;
    border-radius: 6px;
    padding: 6px 10px;
    cursor: pointer;
  }
  .invalidate-popover .modal-actions button:hover {
    background: #484848;
  }

  /* Seta a apontar para os botões */
  .invalidate-popover .popover-arrow {
    position: absolute;
    bottom: -8px; /* aparece por baixo da caixa, a 8px */
    left: 50%;
    transform: translateX(-50%);
    width: 14px;
    height: 14px;
    background: #2b2b2b;
    border-left: 1px solid rgba(255,255,255,0.12);
    border-bottom: 1px solid rgba(255,255,255,0.12);
    transform-origin: center;
    rotate: 45deg;
    box-shadow: 4px 4px 12px rgba(0,0,0,0.3);
  }

  /* animação subtil */
  @keyframes popIn {
    from { opacity: 0; transform: translateY(4px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .untrackable-overlay {
    position: absolute;
    top: 14px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 10;

    padding: 6px 18px;
    border-radius: 8px;

    font-size: 14px;
    font-weight: 800;
    letter-spacing: 2px;
    text-transform: uppercase;

    color: #fff;
    background: rgba(127, 29, 29, 0.92); /* vermelho escuro */
    border: 1px solid rgba(255,255,255,0.2);

    box-shadow:
      0 6px 18px rgba(0,0,0,0.45),
      inset 0 0 0 1px rgba(0,0,0,0.25);

    pointer-events: none;
    user-select: none;
  }
</style>
